import React from 'react';
import { motion } from 'framer-motion';
interface MoneyVisualProps {
  amount: number; // In centen
  breakdown?: MoneyBreakdown;
  showCoins?: boolean;
  showBills?: boolean;
  interactive?: boolean;
  label?: string;
}
interface MoneyBreakdown {
  bills: {
    value: number;
    count: number;
  }[];
  coins: {
    value: number;
    count: number;
  }[];
}
// Euro biljetten en munten
const EURO_BILLS = [500, 200, 100, 50, 20, 10, 5]; // In euro
const EURO_COINS = [200, 100, 50, 20, 10, 5, 2, 1]; // In centen
/**
 * Automatisch geld verdelen in biljetten en munten
 */
function autoBreakdown(cents: number): MoneyBreakdown {
  let remaining = cents;
  const bills: {
    value: number;
    count: number;
  }[] = [];
  const coins: {
    value: number;
    count: number;
  }[] = [];
  // Biljetten (in euro)
  for (const billValue of EURO_BILLS) {
    const billCents = billValue * 100;
    const count = Math.floor(remaining / billCents);
    if (count > 0) {
      bills.push({
        value: billValue,
        count
      });
      remaining -= count * billCents;
    }
  }
  // Munten (in centen)
  for (const coinValue of EURO_COINS) {
    const count = Math.floor(remaining / coinValue);
    if (count > 0) {
      coins.push({
        value: coinValue,
        count
      });
      remaining -= count * coinValue;
    }
  }
  return {
    bills,
    coins
  };
}
/**
 * Formatteer bedrag naar euro weergave
 */
function formatMoney(cents: number): string {
  const euros = Math.floor(cents / 100);
  const remainingCents = cents % 100;
  if (remainingCents === 0) {
    return `€${euros},-`;
  }
  return `€${euros},${remainingCents.toString().padStart(2, '0')}`;
}
/**
 * Krijg kleur voor biljet
 */
function getBillColor(value: number): string {
  switch (value) {
    case 5:
      return 'from-gray-400 to-gray-500';
    case 10:
      return 'from-red-400 to-red-500';
    case 20:
      return 'from-blue-400 to-blue-500';
    case 50:
      return 'from-orange-400 to-orange-500';
    case 100:
      return 'from-green-400 to-green-500';
    case 200:
      return 'from-yellow-400 to-yellow-500';
    case 500:
      return 'from-purple-400 to-purple-500';
    default:
      return 'from-gray-400 to-gray-500';
  }
}
/**
 * Krijg kleur voor munt
 */
function getCoinColor(value: number): string {
  if (value >= 100) {
    // Gouden munten (€1, €2)
    return 'from-yellow-300 to-yellow-500';
  } else if (value >= 10) {
    // Gouden munten (10c, 20c, 50c)
    return 'from-yellow-200 to-yellow-400';
  } else {
    // Koperen munten (1c, 2c, 5c)
    return 'from-orange-300 to-orange-400';
  }
}
/**
 * Formatteer munt waarde
 */
function formatCoinValue(cents: number): string {
  if (cents >= 100) {
    return `€${cents / 100}`;
  }
  return `${cents}¢`;
}
export function MoneyVisual({
  amount,
  breakdown,
  showCoins = true,
  showBills = true,
  interactive = false,
  label
}: MoneyVisualProps) {
  const actualBreakdown = breakdown || autoBreakdown(amount);
  return <div className="w-full" data-id="element-1184">
      {/* Header met totaal bedrag */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 mb-4 border-2 border-emerald-200" data-id="element-1185">
        <div className="flex items-center justify-between" data-id="element-1186">
          <span className="text-sm font-medium text-emerald-900" data-id="element-1187">
            {label || 'Totaal bedrag:'}
          </span>
          <span className="text-2xl font-bold text-emerald-700" data-id="element-1188">
            {formatMoney(amount)}
          </span>
        </div>
      </div>

      <div className="space-y-6" data-id="element-1189">
        {/* Biljetten */}
        {showBills && actualBreakdown.bills.length > 0 && <div data-id="element-1190">
            <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2" data-id="element-1191">
              💵 Biljetten
            </h4>
            <div className="space-y-3" data-id="element-1192">
              {actualBreakdown.bills.map((bill, index) => <motion.div key={`bill-${bill.value}`} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.1
          }} className="flex items-center gap-3" data-id="element-1193">
                  {/* Biljet visueel */}
                  <div className="flex gap-2" data-id="element-1194">
                    {Array.from({
                length: Math.min(bill.count, 5)
              }).map((_, i) => <motion.div key={i} whileHover={interactive ? {
                scale: 1.05,
                rotate: 2
              } : {}} className={`w-20 h-12 rounded-lg bg-gradient-to-br ${getBillColor(bill.value)} shadow-md flex items-center justify-center border-2 border-white/30`} data-id="element-1195">
                        <span className="text-white font-bold text-lg" data-id="element-1196">
                          €{bill.value}
                        </span>
                      </motion.div>)}
                    {bill.count > 5 && <div className="flex items-center justify-center px-3 text-slate-600 font-medium" data-id="element-1197">
                        +{bill.count - 5} meer
                      </div>}
                  </div>

                  {/* Telling */}
                  <div className="flex-1 text-right" data-id="element-1198">
                    <span className="text-sm text-slate-600" data-id="element-1199">
                      {bill.count} × €{bill.value} ={' '}
                      <strong className="text-slate-900" data-id="element-1200">
                        €{bill.count * bill.value}
                      </strong>
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>}

        {/* Munten */}
        {showCoins && actualBreakdown.coins.length > 0 && <div data-id="element-1201">
            <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2" data-id="element-1202">
              🪙 Munten
            </h4>
            <div className="space-y-3" data-id="element-1203">
              {actualBreakdown.coins.map((coin, index) => <motion.div key={`coin-${coin.value}`} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: (actualBreakdown.bills.length + index) * 0.1
          }} className="flex items-center gap-3" data-id="element-1204">
                  {/* Munten visueel */}
                  <div className="flex gap-2 flex-wrap" data-id="element-1205">
                    {Array.from({
                length: Math.min(coin.count, 10)
              }).map((_, i) => <motion.div key={i} whileHover={interactive ? {
                scale: 1.1,
                rotate: 180
              } : {}} className={`w-10 h-10 rounded-full bg-gradient-to-br ${getCoinColor(coin.value)} shadow-md flex items-center justify-center border-2 border-white/50`} data-id="element-1206">
                        <span className="text-white font-bold text-xs" data-id="element-1207">
                          {formatCoinValue(coin.value)}
                        </span>
                      </motion.div>)}
                    {coin.count > 10 && <div className="flex items-center justify-center px-2 text-slate-600 font-medium text-sm" data-id="element-1208">
                        +{coin.count - 10}
                      </div>}
                  </div>

                  {/* Telling */}
                  <div className="flex-1 text-right" data-id="element-1209">
                    <span className="text-sm text-slate-600" data-id="element-1210">
                      {coin.count} × {formatCoinValue(coin.value)} ={' '}
                      <strong className="text-slate-900" data-id="element-1211">
                        {formatCoinValue(coin.count * coin.value)}
                      </strong>
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>}
      </div>

      {/* Didactische tip */}
      <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-200" data-id="element-1212">
        <p className="text-xs text-blue-900" data-id="element-1213">
          💡 <strong data-id="element-1214">Handig om te weten:</strong> We gebruiken eerst de grote
          biljetten, dan de kleine biljetten, en daarna de munten. Zo heb je het
          minste aantal briefjes en muntjes nodig!
        </p>
      </div>
    </div>;
}